import { Button, Chip, Tooltip } from "@heroui/react";
import { Eye } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { CartViewModal } from "./modal/view";

import type { DataGridColumn } from "@/components/kit";

import { DataTable, SearchBox } from "@/components/kit";
import {
  PersonCell,
  Toolbar,
  aed,
  formatDayTime,
} from "@/components/sales/shared";
import { UserCart } from "@/types/cart";

export interface CartTableProps {
  items: UserCart[];
  page: number;
  totalCarts: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  searchTerm: string;
  handleSearchTermChange: (searchTerm: string) => void;
  limit?: number;
  onLimitChange?: (limit: number) => void;
}

export const CartListTable = ({
  searchTerm,
  handleSearchTermChange,
  items,
  page,
  totalCarts,
  changePage,
  isLoading,
  limit = 10,
  onLimitChange,
}: CartTableProps) => {
  const [selectedCart, setSelectedCart] = useState<UserCart | null>(null);

  const handleViewCart = useCallback(
    (cart: UserCart) => setSelectedCart(cart),
    [],
  );

  const columns = useMemo<DataGridColumn<UserCart>[]>(
    () => [
      {
        id: "user",
        header: "Customer",
        isRowHeader: true,
        minWidth: 240,
        cell: (cart) => (
          <PersonCell
            firstName={cart.user.firstName}
            lastName={cart.user.lastName}
            phoneNumber={cart.user.phoneNumber}
          />
        ),
      },
      {
        id: "items",
        header: "Items",
        cell: (cart) => (
          <div className="flex items-center gap-2 tabular-nums">
            {cart.items.length}
            {cart.specialItems.length > 0 ? (
              <Chip color="accent" variant="soft">
                {cart.specialItems.length} special
              </Chip>
            ) : null}
          </div>
        ),
      },
      {
        id: "total",
        header: "Total",
        align: "end",
        minWidth: 260,
        cell: (cart) => (
          <div className="flex flex-col items-end py-1 leading-snug tabular-nums">
            <span className="font-medium">
              {cart.total ? aed(cart.total) : "N/A"}
            </span>
            <span className="text-muted text-xs">
              Subtotal {cart.subTotal.toFixed(2)} · VAT {cart.vat.toFixed(2)} ·
              Delivery {cart.deliveryFee.toFixed(2)}
            </span>
          </div>
        ),
      },
      {
        id: "remainingToMinimum",
        header: "To minimum",
        cell: (cart) =>
          cart.remainingToMinimum > 0 ? (
            <Chip color="warning" variant="soft">
              {aed(cart.remainingToMinimum)} left
            </Chip>
          ) : (
            <Chip color="success" variant="soft">
              Minimum met
            </Chip>
          ),
      },
      {
        id: "updatedAt",
        header: "Updated",
        cell: (cart) => (
          <span className="tabular-nums">{formatDayTime(cart.updatedAt)}</span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "end",
        width: 64,
        cell: (cart) => (
          <Tooltip delay={400}>
            <Button
              isIconOnly
              aria-label="View cart"
              size="sm"
              variant="ghost"
              onPress={() => handleViewCart(cart)}
            >
              <Eye className="size-4" />
            </Button>
            <Tooltip.Content>View cart</Tooltip.Content>
          </Tooltip>
        ),
      },
    ],
    [handleViewCart],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Toolbar>
        <SearchBox
          className="w-80"
          placeholder="Search carts"
          value={searchTerm}
          onChange={handleSearchTermChange}
        />
      </Toolbar>

      <DataTable
        compact
        columns={columns}
        empty={{
          title: "No carts found",
          description: "Try a different search.",
        }}
        getRowId={(cart) => cart.id}
        isLoading={isLoading && items.length === 0}
        label="Carts"
        pagination={{
          page,
          pageSize: limit,
          total: totalCarts,
          onPageChange: changePage,
          itemLabel: "carts",
          onPageSizeChange: onLimitChange,
          pageSizes: [10, 20, 50, 100],
        }}
        rows={items}
      />

      <CartViewModal
        cart={selectedCart}
        isOpen={selectedCart !== null}
        onClose={() => setSelectedCart(null)}
      />
    </div>
  );
};
